import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface CurrencySetting {
  currencyCode: string;
  currencySymbol: string;
  currencyName: string;
}

interface CurrencyContextType {
  currencySetting: CurrencySetting;
  formatCurrency: (amount: number) => string;
  refreshCurrencySetting: () => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [currencySetting, setCurrencySetting] = useState<CurrencySetting>({
    currencyCode: 'JPY',
    currencySymbol: '¥',
    currencyName: '日本円'
  });

  const fetchCurrencySetting = async () => {
    if (!user || !token) return;

    try {
      const response = await api.get('/currency-settings');
      setCurrencySetting(response.data);
    } catch (error) {
      console.error('通貨設定取得エラー:', error);
    }
  };

  const formatCurrency = (amount: number): string => {
    const locale = currencySetting.currencyCode === 'JPY' ? 'ja-JP' : 
                   currencySetting.currencyCode === 'THB' ? 'th-TH' :
                   currencySetting.currencyCode === 'USD' ? 'en-US' :
                   currencySetting.currencyCode === 'EUR' ? 'de-DE' :
                   currencySetting.currencyCode === 'GBP' ? 'en-GB' :
                   currencySetting.currencyCode === 'CNY' ? 'zh-CN' :
                   currencySetting.currencyCode === 'KRW' ? 'ko-KR' :
                   'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencySetting.currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: currencySetting.currencyCode === 'JPY' || currencySetting.currencyCode === 'KRW' ? 0 : 2
      }).format(amount);
    } catch (error) {
      // Fallback to simple format if Intl fails
      return `${currencySetting.currencySymbol}${amount.toLocaleString()}`;
    }
  };

  const refreshCurrencySetting = () => {
    fetchCurrencySetting();
  };

  useEffect(() => {
    if (user && token) {
      fetchCurrencySetting();
    }
  }, [user, token]);

  const value: CurrencyContextType = {
    currencySetting,
    formatCurrency,
    refreshCurrencySetting
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};